import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

interface FamilyTab {
  key: string;
  label: string;
  path: string | null;
}

const FAMILY_TABS: Record<string, FamilyTab[]> = {
  performance: [
    { key: "kpis", label: "KPIs", path: "kpi" },
    { key: "waterfall", label: "Loss waterfall", path: "waterfall" },
    { key: "eva", label: "Expected against actual", path: null },
    { key: "benchmarking", label: "Benchmarking", path: null },
    { key: "plan", label: "Plan and forecast", path: null },
    { key: "attainment", label: "Attainment by contract", path: null },
  ],
  ledgers: [
    { key: "invoices", label: "Invoices", path: null },
    { key: "purchase-orders", label: "Purchase orders", path: null },
    { key: "revenue", label: "Revenue", path: null },
    { key: "tariff", label: "Tariff schedule", path: null },
    { key: "receipts", label: "Receipts", path: null },
  ],
  work: [
    { key: "work-orders", label: "Work orders", path: null },
    { key: "scheduling", label: "Scheduling", path: null },
    { key: "sla", label: "SLA / Penalties", path: null },
    { key: "team", label: "Team", path: null },
    { key: "vendors", label: "Vendors", path: null },
  ],
  docs: [
    { key: "library", label: "Library", path: null },
    { key: "templates", label: "Templates", path: null },
  ],
  /* Admin (IA §3). A screen reached only by its address is not done, so each
     Admin page that EXISTS gets a real tab here and nothing else does: the five
     `path: null` stubs this row used to advertise (Users, Roles, Audit log,
     Billing, Integrations) offered no way in and Billing was not even an IA
     code. Admin holds nine IA pages against a seven-tab limit, so the row ends
     in More and the pages that are not built yet are named there instead of
     posing as tabs (the house pattern — see SiteMoreMenu). */
  admin: [
    { key: "entitlements", label: "Entitlements", path: "admin" },
    { key: "organisations", label: "Organisations", path: "admin/organisations" },
    { key: "people", label: "People", path: "admin/organisations/people" },
    { key: "configuration", label: "Configuration", path: "admin/configuration" },
    { key: "onboarding", label: "Onboarding", path: "admin/assets/onboard" },
    { key: "notifications", label: "Notifications", path: "notifications" },
  ],
};

/** Families whose row ends in More, with the pages the menu holds. */
const FAMILY_MORE: Record<string, { key: string; label: string }[]> = {
  /* The rest of the nine Admin IA pages. None is built, so each says so rather
     than linking nowhere; a page gains a tab above the moment it exists. */
  admin: [
    { key: "roles", label: "Roles" },
    { key: "audit", label: "Audit log" },
    { key: "integrations", label: "Integrations" },
  ],
};

interface FamilyTabsProps {
  family: string;
  /** Optional overrides; when absent they are derived from the current route. */
  tenant?: string;
  scope?: string;
  site?: string;
}

export function FamilyTabs({ family, tenant, scope, site }: FamilyTabsProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [moreOpen, setMoreOpen] = useState(false);
  const tabs = FAMILY_TABS[family];
  const moreItems = FAMILY_MORE[family];
  if (!tabs || tabs.length <= 1) return null;

  const m = pathname.match(/^\/t\/([^/]+)\/s\/([^/]+)/);
  const resolvedTenant = tenant ?? m?.[1];
  const resolvedScope = scope ?? m?.[2];
  const resolvedSite = site ?? pathname.match(/\/plants\/([^/]+)/)?.[1];
  if (!resolvedTenant || !resolvedScope) return null;

  const basePath = resolvedSite
    ? `/t/${resolvedTenant}/s/${resolvedScope}/plants/${resolvedSite}`
    : `/t/${resolvedTenant}/s/${resolvedScope}`;

  /* One tab is active: the LONGEST path that the address is under. A plain
     prefix test would light Entitlements ("admin") on every page beneath it. */
  const activeKey = tabs
    .filter((t) => {
      if (!t.path) return false;
      const href = `${basePath}/${t.path}`;
      return pathname === href || pathname.startsWith(`${href}/`);
    })
    .sort((a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0))[0]?.key;

  return (
    <div className="sitetabs" style={{ fontSize: 11, gap: 14, borderColor: "var(--sk-color-border-2)" }}>
      {tabs.map((tab) => {
        // UX-REV-001 U5: a feature that is not yet built is NOT shown at all (a
        // greyed stub reads as a broken feature). It appears when it exists. A
        // tier-LOCKED item is different — that keeps a lock + upgrade cue (Rail).
        if (!tab.path) {
          return null;
        }
        const href = `${basePath}/${tab.path}`;
        return (
          <Link key={tab.key} to={href as never} style={{ textDecoration: "none" }}>
            <span
              className={activeKey === tab.key ? "on" : ""}
              style={{ fontSize: 11, padding: "6px 2px 7px" }}
            >
              {tab.label}
            </span>
          </Link>
        );
      })}

      {moreItems && moreItems.length > 0 && (
        <>
          <span
            className="has-menu"
            style={{ fontSize: 11, padding: "6px 2px 7px" }}
            onClick={() => setMoreOpen((v) => !v)}
            role="button"
            tabIndex={0}
            aria-expanded={moreOpen}
            aria-haspopup="menu"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setMoreOpen((v) => !v);
              }
            }}
          >
            More <ChevronDown size={11} style={{ verticalAlign: "middle", display: "inline-block" }} />
          </span>
          {moreOpen && (
            <>
              <button
                type="button"
                aria-label="Close menu"
                style={{ position: "fixed", inset: 0, background: "transparent", border: 0, zIndex: 2 }}
                onClick={() => setMoreOpen(false)}
              />
              <div className="menu">
                {moreItems.map((item) => (
                  <div
                    key={item.key}
                    style={stubMenuStyle}
                    title={`${item.label} — not built yet`}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** A More-menu entry for a page that does not exist yet (as SiteMoreMenu does). */
const stubMenuStyle: React.CSSProperties = {
  opacity: 0.45,
  cursor: "default",
  borderBottom: "1px dashed var(--sk-color-border-2)",
  display: "inline-block",
};
